import { makeStyles, mergeClasses, tokens, Text } from '@coreai/fluentui-react';
import { MOBILE_BREAKPOINT } from './constants';
import { ottoTokens } from '@/theme/ottoTokens';

interface LaneSpec {
  readonly label: string;
  readonly fillWidthPct: number;
  readonly emphasised?: boolean;
  readonly mode: 'blocked' | 'flowing';
  readonly capText: string;
}

const LANES: readonly LaneSpec[] = [
  { label: 'Vendor A', fillWidthPct: 55, mode: 'blocked', capText: '10K runs' },
  { label: 'Vendor B', fillWidthPct: 35, mode: 'blocked', capText: '5K' },
  { label: 'Logic Apps Automation', fillWidthPct: 100, mode: 'flowing', capText: 'Scaling…', emphasised: true },
];

const useStyles = makeStyles({
  section: {
    paddingBlock: '100px',
    borderTopStyle: 'solid',
    borderTopWidth: '1px',
    borderTopColor: tokens.colorNeutralStroke2,
    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
      paddingBlock: '64px',
    },
  },
  inner: {
    maxWidth: '1200px',
    marginInline: 'auto',
    paddingInline: tokens.spacingHorizontalXXL,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '80px',
    alignItems: 'center',
    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
      gridTemplateColumns: '1fr',
      gap: '40px',
      paddingInline: tokens.spacingHorizontalL,
    },
  },
  eyebrow: {
    display: 'block',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    color: ottoTokens.landingAccentTextCool,
    marginBlockEnd: tokens.spacingVerticalM,
  },
  heading: {
    display: 'block',
    fontSize: 'clamp(28px, 4vw, 44px)',
    fontWeight: tokens.fontWeightBold,
    lineHeight: 1.1,
    letterSpacing: '-0.02em',
    color: tokens.colorNeutralForeground1,
    marginBlockEnd: tokens.spacingVerticalL,
  },
  lead: {
    fontSize: tokens.fontSizeBase400,
    lineHeight: 1.55,
    color: tokens.colorNeutralForeground2,
  },
  mockCard: {
    backgroundColor: ottoTokens.landingCardBackground,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusXLarge,
    padding: '28px',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  lane: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  laneLabel: {
    width: '180px',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    fontWeight: tokens.fontWeightMedium,
  },
  laneLabelEmphasised: {
    color: ottoTokens.landingAccentTextCool,
  },
  laneBar: {
    flex: 1,
    height: '32px',
    borderRadius: tokens.borderRadiusMedium,
    overflow: 'hidden',
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    position: 'relative',
  },
  laneBarEmphasised: {
    border: `1px solid ${ottoTokens.landingAccentBorder}`,
  },
  laneFill: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingInline: tokens.spacingHorizontalM,
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
  },
  laneFillBlocked: {
    backgroundImage: ottoTokens.landingBlockedGradient,
    color: tokens.colorNeutralForegroundInverted,
  },
  laneFillFlowing: {
    backgroundImage: ottoTokens.landingPrimaryGradient,
    color: tokens.colorNeutralForegroundInverted,
  },
  caption: {
    fontSize: tokens.fontSizeBase100,
    color: ottoTokens.landingDimText,
    textAlign: 'center',
    marginBlockStart: tokens.spacingVerticalXS,
  },
});

export const NoQuotaSection = () => {
  const styles = useStyles();

  // Soften "No quota walls. Ever." to a defensible scoped claim.
  // PM/legal hasn't sanctioned an absolute promise yet; "scale with you"
  // tells the same story without an unprovable forever-guarantee.
  return (
    <section className={styles.section} aria-labelledby="no-quota-title">
      <div className={styles.inner}>
        <div>
          <Text className={styles.eyebrow}>Built to scale</Text>
          <Text as="h2" id="no-quota-title" className={styles.heading}>
            Scale with you, not against you.
          </Text>
          <Text className={styles.lead}>
            Other platforms throttle or block your workflows when they cross a usage line — the
            moment your business needs them most. Logic Apps Automation scales with you. No stuck
            runs, no surprise pauses.
          </Text>
        </div>
        <div className={styles.mockCard} aria-hidden="true">
          {LANES.map((lane) => (
            <div key={lane.label} className={styles.lane}>
              <Text className={mergeClasses(styles.laneLabel, lane.emphasised && styles.laneLabelEmphasised)}>
                {lane.label}
              </Text>
              <div className={mergeClasses(styles.laneBar, lane.emphasised && styles.laneBarEmphasised)}>
                <div
                  className={mergeClasses(
                    styles.laneFill,
                    lane.mode === 'blocked' ? styles.laneFillBlocked : styles.laneFillFlowing,
                  )}
                  // Width drives the bar fill — it's data, not a static
                  // design value, so an inline style is appropriate here.
                  style={{ width: `${lane.fillWidthPct}%` }}
                >
                  <span>{lane.capText}</span>
                  <span>{lane.mode === 'blocked' ? 'BLOCKED' : 'KEEP GOING'}</span>
                </div>
              </div>
            </div>
          ))}
          <Text className={styles.caption}>Usage-based scaling. No quota cliffs.</Text>
        </div>
      </div>
    </section>
  );
};
